// Passare la sessione dal browser di Phantom a quello normale.
//
// PERCHÉ SERVE
//
// Da telefono il gioco si gioca in Chrome — lì rotazione, joystick e
// scorrimento funzionano da soli — ma la FIRMA del login riesce solo dentro il
// browser interno di Phantom. Il suo deeplink `signMessage` risponde
// `-32603 Unexpected error`: guasto interno di Phantom aperto dal 2024, che
// abbiamo inseguito a lungo escludendo ogni causa dalla nostra parte, fino a
// confrontare la nostra richiesta con la loro app dimostrativa ufficiale.
//
// Sono due browser diversi, con archivi separati: il token emesso nell'uno non
// è leggibile dall'altro. Questo modulo fa il ponte.
//
// COME FUNZIONA
//
//   1. Chrome genera un segreto casuale e se lo tiene.
//   2. Apre il browser di Phantom sulla stessa pagina, portandosi il segreto
//      nell'indirizzo.
//   3. Lì il wallet si collega e firma da solo: è il percorso di sempre, quello
//      che funziona.
//   4. La pagina consegna il token al server, legato a quel segreto.
//   5. Chrome — l'unico a conoscerlo — se lo riprende e la sessione è aperta.
//
// PERCHÉ È SICURO
//
// Il segreto è generato nel browser che poi lo usa, non viaggia su nessun
// canale altrui, vale una volta sola e scade in cinque minuti. Chi non lo
// conosce non ha niente da indovinare (32 byte casuali); chi lo conosce è già
// il legittimo destinatario. Il deposito richiede l'autenticazione, quindi
// nessuno può lasciare un token arbitrario sotto il segreto di un altro.

import { phantomBrowseUrl, browserCorrente } from './phantomMobile';
import { salvaToken } from './authClient';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const CHIAVE_SEGRETO = 'vexel_consegna_segreto';
const PARAM = 'consegna';
// Da quale browser è partito il giro: senza, il viaggio di ritorno non sa dove
// riportare il giocatore.
const PARAM_DA = 'da';

// I pacchetti Android dei browser che sappiamo riaprire. Senza `package=`
// l'intent finisce al gestore predefinito dei link https, che può essere di
// nuovo Phantom: si tornerebbe da dove si è partiti.
const PACCHETTI_ANDROID = {
  chrome: 'com.android.chrome',
  firefox: 'org.mozilla.firefox',
  samsung: 'com.sec.android.app.sbrowser',
  edge: 'com.microsoft.emmx',
};

function leggi(chiave) {
  try { return localStorage.getItem(chiave); } catch (e) { return null; }
}

// 32 byte casuali in esadecimale. `crypto.getRandomValues` è presente in ogni
// browser che ci interessa; senza un generatore crittografico non si procede,
// perché un segreto prevedibile qui varrebbe quanto nessun segreto.
function generaSegreto() {
  const buf = new Uint8Array(32);
  window.crypto.getRandomValues(buf);
  return Array.from(buf, (b) => b.toString(16).padStart(2, '0')).join('');
}

export function segretoInAttesa() {
  return leggi(CHIAVE_SEGRETO);
}

export function dimenticaSegreto() {
  try { localStorage.removeItem(CHIAVE_SEGRETO); } catch (e) {}
}

// Il segreto ricevuto dal browser normale, se siamo la pagina aperta dentro
// Phantom per firmare.
export function segretoDaIndirizzo() {
  try {
    return new URLSearchParams(window.location.search).get(PARAM);
  } catch (e) {
    return null;
  }
}

// Passo 1 e 2: si chiama dal browser normale, dal gestore di un tocco.
export function apriPhantomPerFirmare() {
  const segreto = generaSegreto();
  try { localStorage.setItem(CHIAVE_SEGRETO, segreto); } catch (e) {}

  const base = window.location.origin + window.location.pathname;
  const da = browserCorrente();
  const coda = da ? `&${PARAM_DA}=${encodeURIComponent(da)}` : '';
  return phantomBrowseUrl(`${base}?${PARAM}=${segreto}${coda}`);
}

// IL VIAGGIO DI RITORNO
//
// Finita la firma, il giocatore si trovava nel browser di Phantom con scritto
// «torna al tuo browser» e doveva cambiare app a mano. Qui si prova a farlo per
// lui.
//
// Non è garantito, e va detto: **nessuna pagina può cambiare app da sola**. Può
// solo chiedere al sistema di aprire un indirizzo che appartiene a un'altra
// app, e chi ospita la webview — Phantom — resta libero di non lasciar passare
// la richiesta. Per questo il pulsante di ritorno resta comunque a schermo: se
// anche questa strada fosse chiusa, il giocatore ha davanti l'istruzione.
//
// Il segreto viene tolto dall'indirizzo di ritorno: con quello ancora attaccato
// il browser riaprirebbe la schermata di consegna invece della lobby.
export function urlDiRitorno() {
  try {
    const parametri = new URLSearchParams(window.location.search);
    const da = parametri.get(PARAM_DA);
    if (!da) return null;

    const url = new URL(window.location.href);
    url.searchParams.delete(PARAM);
    url.searchParams.delete(PARAM_DA);
    const senzaSchema = url.host + url.pathname + url.search;

    if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      if (da === 'chrome') return `googlechromes://${senzaSchema}`;
      if (da === 'firefox') return `firefox://open-url?url=${encodeURIComponent(url.href)}`;
      if (da === 'edge') return `microsoft-edge-https://${senzaSchema}`;
      if (da === 'safari') return `x-safari-https://${senzaSchema}`;
      return null;
    }

    const pacchetto = PACCHETTI_ANDROID[da];
    if (!pacchetto) return null;
    return `intent://${senzaSchema}#Intent;scheme=https;package=${pacchetto};end`;
  } catch (e) {
    return null;
  }
}

// COME SI TENTA IL SALTO, E PERCHÉ `location.href` DA SOLO NON BASTA
//
// Dentro una webview non è la pagina a decidere se si cambia app: è chi ospita
// la webview. Su iOS il sistema consegna all'app ospite, insieme
// all'indirizzo, anche il MOTIVO della navigazione — e moltissime app aprono
// verso l'esterno solo quando quel motivo è «l'utente ha toccato un link»
// (`linkActivated`), scartando in silenzio tutto il resto. Un
// `window.location.href` eseguito da JavaScript ricade proprio nel "resto", ed
// è la ragione più probabile per cui il ritorno automatico non partiva e il
// giocatore restava dentro Phantom.
//
// Un `<a>` vero, presente nel documento e cliccato, produce `linkActivated`
// anche quando il clic arriva dal codice: per l'ospite è indistinguibile dal
// dito del giocatore. È quindi il primo tentativo, e `location.href` resta
// solo da ripiego per gli ospiti che invece lo accettano.
//
// Si insiste due volte perché un tentativo soppresso non lascia nessuna
// traccia: non c'è niente da controllare, si può solo riprovare in un altro
// modo. Ma si smette appena la pagina sparisce dalla vista — quello è il
// segnale che il salto è riuscito, e continuare vorrebbe dire richiamare il
// browser addosso a un giocatore che è già di là.
//
// Ritorna la funzione per interrompere i tentativi ancora in coda.
export function tornaAlBrowser(url) {
  if (!url || typeof document === 'undefined') return () => {};

  const timer = [];
  let fermato = false;

  const suCambioVista = () => {
    if (document.visibilityState === 'hidden') stop();
  };

  function stop() {
    if (fermato) return;
    fermato = true;
    timer.forEach(clearTimeout);
    document.removeEventListener('visibilitychange', suCambioVista);
  }

  const conAncora = () => {
    const a = document.createElement('a');
    a.href = url;
    // Attaccato al documento, non staccato: un `<a>` fuori dall'albero non
    // produce una navigazione utente in tutte le webview.
    a.style.position = 'fixed';
    a.style.opacity = '0';
    a.style.pointerEvents = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const conIndirizzo = () => { window.location.href = url; };

  const prova = (tentativo) => {
    if (fermato || document.visibilityState === 'hidden') return;
    try { tentativo(); } catch (e) {}
  };

  // Il PRIMO tentativo è sincrono, e non è un dettaglio: quando questa
  // funzione è chiamata dal gestore di un tocco, rimandarlo anche di un solo
  // giro di eventi butterebbe via il gesto dell'utente — cioè proprio la cosa
  // che rende il salto accettabile.
  document.addEventListener('visibilitychange', suCambioVista);
  prova(conAncora);

  const seguenti = [conIndirizzo, conAncora];
  seguenti.forEach((tentativo, i) => {
    timer.push(setTimeout(() => prova(tentativo), (i + 1) * 700));
  });

  // Finiti i tentativi non resta niente da ascoltare: senza questo, l'ascolto
  // su `visibilitychange` sopravviverebbe alla propria utilità ogni volta che
  // il giocatore tocca il pulsante.
  timer.push(setTimeout(stop, (seguenti.length + 1) * 700));

  return stop;
}

// Passo 4: si chiama dentro il browser di Phantom, a login avvenuto.
export async function consegnaToken(segreto, token) {
  const res = await fetch(`${API_BASE}/api/auth/handoff`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ segreto }),
  });
  if (!res.ok) {
    const dati = await res.json().catch(() => ({}));
    throw new Error(dati.error || 'Session handoff refused');
  }
  return true;
}

// Passo 5: si chiama dal browser normale. Ritorna il wallet a consegna
// avvenuta, null se non è ancora pronta.
export async function riscuotiToken() {
  const segreto = segretoInAttesa();
  if (!segreto) return null;

  const res = await fetch(`${API_BASE}/api/auth/handoff/${segreto}`);
  if (!res.ok) return null;

  const dati = await res.json().catch(() => ({}));
  if (!dati || dati.pending || !dati.token || !dati.wallet) return null;

  salvaToken(dati.wallet, dati.token);
  dimenticaSegreto();
  return dati.wallet;
}
